/**
 * A modal dialog.
 *
 * The integration configuration used to open below the grid, which Damon
 * called correctly: on a page with twenty-odd tiles the form appears somewhere
 * off-screen, and the thing you clicked is still sitting up there looking
 * unchanged. A modal puts the answer where the question was.
 *
 * UNSAVED WORK BLOCKS THE CASUAL DISMISSALS. Every field in here is a password
 * box that shows nothing back, so a credential lost to a stray click on the
 * backdrop is a credential typed twice - and the second attempt is where the
 * typo goes. Escape and backdrop stop working the moment anything is typed;
 * the explicit close button always works, and warns.
 */

import { useEffect, useRef } from 'react';

export function Modal({ title, subtitle, onClose, dirty, children }: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  /** Something has been typed and not saved. */
  dirty?: boolean;
  children: React.ReactNode;
}) {
  const panel = useRef<HTMLDivElement | null>(null);
  // Read through a ref so the Escape handler always sees the CURRENT value.
  // Binding `dirty` into the effect would either re-register the listener on
  // every keystroke or capture a stale `false` and throw the work away.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !dirtyRef.current) onClose();
    };
    document.addEventListener('keydown', onKey);
    // The page behind a modal must not scroll: on a long integrations page,
    // a scroll gesture over the backdrop otherwise moves the grid instead.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // Focus the panel so Escape and Tab start inside the dialog rather than on
  // whatever tile was clicked.
  useEffect(() => { panel.current?.focus(); }, []);

  function close() {
    if (dirtyRef.current &&
        !window.confirm('You have typed something that has not been saved. Close anyway?')) return;
    onClose();
  }

  return (
    <div
      className="modal-back"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !dirty) onClose(); }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={panel}>
        <div className="modal-head">
          <div>
            <div className="modal-title">{title}</div>
            {subtitle && <div className="mono dim" style={{ fontSize: 11 }}>{subtitle}</div>}
          </div>
          <button className="modal-x" onClick={close} aria-label="Close">×</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
